import React from 'react';
import { cn } from '../../utils/cn.ts';
import { GlassRegular } from './GlassSurface/index.ts';

interface ToolActionBarProps {
    /** Tool name displayed on left */
    title: string;
    /** Test id for the outer toolbar surface */
    testId?: string;
    /** Test id for the title */
    titleTestId?: string;
    /** Optional left-aligned actions (filters, toggles) */
    leftActions?: React.ReactNode;
    /** Optional split view toggle button (shown when workspace has a layout) */
    splitToggle?: React.ReactNode;
    /** Optional right-aligned actions (undo, redo, print) */
    rightActions?: React.ReactNode;
    /** Additional className for customization */
    className?: string;
}

/**
 * Tool Action Bar - Horizontal bar for tool-scoped actions
 * 
 * Provides a consistent pattern across all tools for:
 * - Tool identification (title)
 * - Left side: tool-specific controls (filters, view toggles)
 * - Right side: common actions (undo, redo, print)
 * 
 * Usage:
 * ```tsx
 * <ToolActionBar
 *   title="Run Sheet"
 *   rightActions={
 *     <>
 *       <UndoRedoButtons {...undoProps} />
 *       <PrintButton />
 *     </>
 *   }
 * />
 * ```
 */
export const ToolActionBar: React.FC<ToolActionBarProps> = ({
    title,
    testId,
    titleTestId,
    leftActions,
    splitToggle,
    rightActions,
    className = ''
}) => {
    return (
        <GlassRegular
            className={cn(
                'app-tool-action-bar',
                'flex items-center justify-between gap-4 px-4 py-2.5',
                'shrink-0',  // Prevent shrinking, stays at fixed position in flex layout
                'print:hidden',
                'rounded-none! rounded-bl-[12px]!',  // flush edges except bottom-left corner
                className
            )}
            data-app-toolbar="true"
            data-testid={testId}
        >
            {/* Left: Title + Tool-specific actions */}
            <div className="flex min-w-0 shrink-0 items-center gap-4">
                <h2
                    className="shrink-0 whitespace-nowrap font-semibold text-gray-900 text-lg dark:text-white"
                    data-testid={titleTestId}
                >
                    {title}
                </h2>
                {leftActions ? <div className="flex min-w-0 shrink-0 items-center gap-2">
                        {leftActions}
                    </div> : null}
                {splitToggle ? <div className="ml-2 flex items-center">
                        {splitToggle}
                    </div> : null}
            </div>

            {/* Right: Common actions (undo, redo, print) */}
            {rightActions ? <div className="flex shrink-0 items-center gap-2">
                    {rightActions}
                </div> : null}
        </GlassRegular>
    );
};














